import React, { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ArrowLeft, Mail, AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useApi } from "../hooks/useApi";
import { apiService } from "../services/apiService";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [userType, setUserType] = useState("runner");
  const { execute: executeForgotPassword, loading } = useApi();
  const [emailSent, setEmailSent] = useState(false);
  const [errors, setErrors] = useState({
    email: "",
    userType: ""
  });

  const validateEmail = (email) => {
    if (!email.trim()) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "Please enter a valid email address";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const emailError = validateEmail(email);
    setErrors({
      email: emailError,
      userType: ""
    });

    if (emailError) return;

    try {
      await executeForgotPassword(
        () => apiService.auth.forgotPassword(email.trim(), userType),
        { 
          showSuccessToast: true,
          successMessage: "Password reset link sent to your email!"
        }
      );
      
      setEmailSent(true);
    } catch (error) {
      // Error is already handled by useApi hook
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-runcards-background text-runcards-text-primary flex items-center justify-center px-4">
        <Card className="w-full max-w-md bg-runcards-foreground/80 backdrop-blur-xl border border-runcards-border/20">
          <CardContent className="p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-runcards-success/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-runcards-success" />
              </div>
              
              <h2 className="text-2xl font-runcards-bold text-runcards-text-primary mb-4">
                Check Your Email
              </h2>
              
              <p className="text-runcards-text-tertiary mb-6 leading-relaxed font-runcards">
                We've sent a password reset link to <strong className="text-runcards-text-primary">{email}</strong>. 
                Please check your email and click the link to reset your password.
              </p>
              
              <div className="bg-runcards-warning/20 border border-runcards-warning/30 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-runcards-warning mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-runcards-warning font-runcards">
                    <p className="font-runcards-bold mb-1">Important:</p>
                    <p>The reset link will expire in 1 hour for security reasons.</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <Button
                  onClick={() => {
                    setEmailSent(false);
                    setEmail("");
                  }}
                  className="w-full bg-runcards-background/50 hover:bg-runcards-background/70 text-runcards-text-primary border border-runcards-border/20 font-runcards"
                >
                  Send Another Email
                </Button>
                
                <Link to="/auth">
                  <Button
                    variant="outline"
                    className="w-full border-runcards-border/20 text-runcards-text-primary hover:bg-runcards-background/50 font-runcards"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-runcards-background text-runcards-text-primary flex items-center justify-center px-4">
      <Card className="w-full max-w-md bg-runcards-foreground/80 backdrop-blur-xl border border-runcards-border/20">
        <CardHeader className="text-center pb-4">
          <CardTitle className="text-2xl font-runcards-bold text-runcards-text-primary">
            Forgot Password
          </CardTitle>
          <p className="text-runcards-text-tertiary text-sm font-runcards">
            Enter your email and we'll send you a reset link
          </p>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-runcards-text-primary font-runcards-bold" style={{ color: '#ffffff' }}>
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-runcards-text-tertiary w-5 h-5" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  style={{ color: '#1a1d26' }}
                  className="pl-10 bg-runcards-background/50 border-runcards-border/20 placeholder:text-gray-500 focus:bg-runcards-background/70 focus:border-runcards-border/40 font-runcards"
                />
              </div>
              {errors.email && (
                <p className="text-runcards-error text-sm font-runcards">
                  {errors.email}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="userType" className="text-runcards-text-primary font-runcards-bold" style={{ color: '#ffffff' }}>
                Account Type
              </Label>
              <Select value={userType} onValueChange={setUserType}>
                <SelectTrigger className="bg-runcards-background/50 border-runcards-border/20 focus:bg-runcards-background/70 focus:border-runcards-border/40" style={{ color: '#ffffff' }}>
                  <SelectValue placeholder="Select account type" />
                </SelectTrigger>
                <SelectContent className="bg-runcards-background/90 border-runcards-border/20">
                  <SelectItem value="runner" className="text-runcards-text-primary hover:bg-white/10 font-runcards">
                    Runner
                  </SelectItem>
                  <SelectItem value="runclub" className="text-runcards-text-primary hover:bg-white/10 font-runcards">
                    Run Club
                  </SelectItem>
                </SelectContent>
              </Select>
              {errors.userType && (
                <p className="text-runcards-error text-sm font-runcards">
                  {errors.userType}
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-runcards-text-primary text-runcards-background hover:bg-runcards-text-primary/90 font-runcards-bold"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending Reset Link...
                </>
              ) : (
                "Send Reset Link"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/auth" className="text-runcards-text-tertiary hover:text-runcards-text-primary text-sm font-runcards">
              <ArrowLeft className="w-4 h-4 inline mr-1" />
              Back to Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
